import { useEffect, useState } from "react";
import { finAPI, fmtDate, SEVERITY_STYLE, STATUS_STYLE } from "./finApi";
import { Filter, ShieldAlert, RefreshCw, Moon, Check, BedDouble } from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";
import { NightAuditCatchUp } from "./NightAuditCatchUp";

export default function FinanceAudit() {
  const [flags, setFlags] = useState([]);
  const [audits, setAudits] = useState([]);
  const [status, setStatus] = useState("open");
  const [scanning, setScanning] = useState(false);
  const [running, setRunning] = useState(false);
  const [postingNights, setPostingNights] = useState(false);
  const [nightsResult, setNightsResult] = useState(null);
  const [businessDate, setBusinessDate] = useState("");
  const [stale, setStale] = useState([]);

  const load = () => {
    finAPI.flags(status ? { status } : {}).then(setFlags).catch(() => {});
    finAPI.nightAudits().then(setAudits).catch(() => {});
    finAPI.businessDate().then(d => setBusinessDate(prev => prev || d.business_date));
    finAPI.nightAuditStale().then(d => setStale(d.stale || [])).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const runScan = async () => {
    setScanning(true);
    try { const r = await finAPI.scan(); alert(`Scan complete. ${r.created_flags} new flag(s).`); load(); }
    catch (e) { alert(e?.response?.data?.detail || "Scan failed"); }
    finally { setScanning(false); }
  };
  const resolve = async (f) => {
    const res = window.prompt("Resolution notes:"); if (!res) return;
    try { await finAPI.resolveFlag(f.id, { resolution: res }); load(); }
    catch (e) { alert(e?.response?.data?.detail || "Failed"); }
  };
  const runAudit = async () => {
    if (!businessDate) { alert("Choose a business date."); return; }
    if (!window.confirm(`Run Night Audit for ${businessDate}? This closes the books for that date and is irreversible.`)) return;
    setRunning(true);
    try {
      const r = await finAPI.runNightAudit({ business_date: businessDate });
      if (r.status === "already_closed") alert(`${businessDate} was already closed — nothing posted.`);
      load();
    }
    catch (e) { const d = e?.response?.data; alert(d?.message || d?.detail?.message || d?.detail || "Night audit failed"); }
    finally { setRunning(false); }
  };
  const postRoomNights = async () => {
    if (!businessDate) { alert("Choose a business date."); return; }
    setPostingNights(true);
    try {
      const r = await finAPI.postRoomNights({ business_date: businessDate });
      setNightsResult(r);
    } catch (e) { alert(e?.response?.data?.detail || "Room night posting failed"); }
    finally { setPostingNights(false); }
  };

  return (
    <div className="space-y-5" data-testid="fin-audit-page">
      {stale.length > 0 && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-3 text-[13px] text-red-800 flex items-center gap-3" data-testid="night-audit-overdue-banner">
          <ShieldAlert className="w-4 h-4 shrink-0" />
          <div><b>Night audit overdue.</b> {stale.length} business date{stale.length > 1 ? "s" : ""} open for more than 26 h — oldest {stale[0].business_day} ({stale[0].hours_open} h). Use “Catch up missed days” below.</div>
        </div>
      )}
      {/* NIGHT AUDIT CARD */}
      <div className="on-dark bg-gradient-to-br from-[#0F1530] to-[#070B1F] rounded-2xl p-6 border border-white/10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-300 font-medium flex items-center gap-2">
              <Moon className="w-3.5 h-3.5"/> Night Audit — Close Business Day
            </div>
            <div className="mt-2 text-[13px] text-slate-200">
              Accrues tonight&apos;s room charge for every in-house guest, runs the revenue audit, snapshots the day&apos;s ledger,
              and marks the business day as closed. Room-night posting is idempotent per stay per date.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DatePicker value={(businessDate || '').slice(0,10)} onChange={(_v) => setBusinessDate(_v)} data-testid="fin-audit-date"/>
            <button onClick={postRoomNights} disabled={postingNights} data-testid="fin-post-room-nights"
              className="px-4 py-2 rounded-lg border border-white/20 text-white text-[13px] font-medium hover:bg-white/10 disabled:opacity-60 inline-flex items-center gap-2 whitespace-nowrap">
              <BedDouble className="w-4 h-4"/> {postingNights ? "Posting…" : "Post Room Nights Now"}
            </button>
            <button onClick={runAudit} disabled={running} data-testid="fin-run-night-audit"
              className="px-4 py-2 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-semibold hover:brightness-105 disabled:opacity-60 shadow-sm whitespace-nowrap">
              {running ? "Closing…" : "Close Day"}
            </button>
          </div>
        </div>
        {nightsResult && (
          <div className="mt-4 rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-[13px] text-slate-200" data-testid="fin-room-nights-result">
            <span className="font-semibold text-white">{nightsResult.business_day}</span>
            {" · "}{nightsResult.in_house_stays} in-house ·{" "}
            <span className="text-emerald-300">{nightsResult.posted} posted</span>
            {" · "}{nightsResult.already_posted} already posted · {nightsResult.skipped_count} skipped
            {nightsResult.posted > 0 && <> · ₹{(nightsResult.posted_gross_minor / 100).toLocaleString("en-IN")} accrued</>}
            {nightsResult.skipped?.length > 0 && (
              <div className="mt-1 text-[12px] text-amber-200">
                {nightsResult.skipped.map(s => `${s.stay_code || s.stay_id}: ${s.reason}`).join(" · ")}
              </div>
            )}
          </div>
        )}
      </div>

      <NightAuditCatchUp stale={stale} onDone={load} />

      {/* NIGHT AUDIT HISTORY */}
      {audits.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100">
            <h3 className="font-display text-[14px] font-semibold text-hocp-navy">Closed Business Days</h3>
          </div>
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2">Business Date</th>
                <th className="text-right px-4 py-2">Revenue</th>
                <th className="text-right px-4 py-2">Payments</th>
                <th className="text-right px-4 py-2">Open Flags</th>
                <th className="text-left px-4 py-2">Closed By</th>
                <th className="text-left px-4 py-2">Closed At</th>
                <th className="text-left px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {audits.map(a => {
                const st = STATUS_STYLE[a.status] || { chip: "bg-slate-100 text-slate-700", label: a.status };
                return (
                  <tr key={a.id} data-testid={`fin-audit-row-${a.business_date}`}>
                    <td className="px-4 py-2 font-mono tabular-nums text-hocp-navy">{a.business_date}</td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums">₹{Number(a.total_revenue || 0).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{a.payment_count}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{a.open_flags_at_close}</td>
                    <td className="px-4 py-2 text-slate-600">{a.completed_by_email}</td>
                    <td className="px-4 py-2 text-slate-500 text-[12px]">{fmtDate(a.completed_at)}</td>
                    <td className="px-4 py-2"><span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium ${st.chip}`}>{st.label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* FLAGS */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-[12px] text-slate-500"><Filter className="w-4 h-4"/> Filter:</div>
        <select value={status} onChange={e => setStatus(e.target.value)} data-testid="fin-flag-filter"
          className="px-3 py-1.5 border border-slate-200 rounded-lg text-[12px] bg-white">
          <option value="open">Open flags</option>
          <option value="resolved">Resolved</option>
          <option value="">All</option>
        </select>
        <div className="flex-1"/>
        <button onClick={runScan} disabled={scanning} data-testid="fin-scan-btn"
          className="px-4 py-2 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[12px] font-medium inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-60">
          <RefreshCw className={`w-4 h-4 ${scanning ? "animate-spin" : ""}`}/> {scanning ? "Scanning…" : "Run Scan Now"}
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {flags.length === 0 ? (
          <div className="p-12 text-center" data-testid="fin-flags-empty">
            <ShieldAlert className="w-10 h-10 text-slate-300 mx-auto mb-3"/>
            <div className="text-[13px] text-slate-500">No revenue flags. Books look clean.</div>
          </div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5">Severity</th>
                <th className="text-left px-4 py-2.5">Kind</th>
                <th className="text-left px-4 py-2.5">Message</th>
                <th className="text-left px-4 py-2.5">Business Date</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {flags.map(f => {
                const sev = SEVERITY_STYLE[f.severity] || SEVERITY_STYLE.medium;
                const st  = STATUS_STYLE[f.status] || { chip: "bg-slate-100 text-slate-700", label: f.status };
                return (
                  <tr key={f.id} className="hover:bg-slate-50" data-testid={`fin-flag-row-${f.id}`}>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium ${sev.chip}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${sev.dot}`}/>
                        {f.severity}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-[12px] text-slate-700">{f.kind}</td>
                    <td className="px-4 py-2.5 text-slate-700">{f.message}</td>
                    <td className="px-4 py-2.5 tabular-nums text-slate-600">{f.business_date}</td>
                    <td className="px-4 py-2.5"><span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium ${st.chip}`}>{st.label}</span></td>
                    <td className="px-4 py-2.5 text-right">
                      {f.status === "open" && (
                        <button onClick={() => resolve(f)} data-testid={`fin-flag-resolve-${f.id}`}
                          className="text-[12px] text-emerald-700 hover:text-emerald-900 font-medium inline-flex items-center gap-1">
                          <Check className="w-3.5 h-3.5"/>Resolve
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
